import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, Target, Bell, Newspaper, CalendarClock, Mail, Zap,
  LineChart, ShieldAlert, ArrowRight, Infinity as InfinityIcon, Sliders,
} from "lucide-react";

const FEATURES = [
  {
    icon: LineChart,
    title: "Live breakout & support levels",
    body: "Every stock you track gets auto-calculated 20-day and 50-day breakout and support levels so you know exactly when a trend is turning. Override any level with your own number anytime.",
  },
  {
    icon: Target,
    title: "Sell targets T1 / T2 / T3",
    body: "Three profit targets are suggested using the measured-move formula T = Breakout + n × (Breakout − Support), so you can scale out instead of selling the whole position too early.",
  },
  {
    icon: TrendingUp,
    title: "Real-time quotes, unlimited stocks",
    body: "Prices and volume refresh every 60 seconds while the app is open. Powered by Yahoo Finance — no API key, no daily cap, add as many tickers as you want.",
  },
  {
    icon: Bell,
    title: "Smart in-app + email alerts",
    body: "Toasts and emails fire the moment a stock breaks out, breaks down, hits a sell target or reaches a profit zone. A 6-hour cooldown means no spam when prices chop.",
  },
  {
    icon: Newspaper,
    title: "News with sentiment scoring",
    body: "Headlines are pulled hourly per stock and auto-labelled Bullish / Bearish / Neutral using a financial-vocab engine. Strongly bullish or bearish news fires a dedicated alert.",
  },
  {
    icon: CalendarClock,
    title: "Earnings countdown",
    body: "An amber EARNINGS badge appears on any stock reporting within 7 days so you can tighten stops or trim ahead of the volatility. Consensus EPS shown in the detail drawer.",
  },
  {
    icon: Mail,
    title: "Daily morning digest",
    body: "One scannable email at 7am ET every day: all overnight bullish and bearish headlines across your entire watchlist, plus any earnings landing in the next 7 days.",
  },
  {
    icon: Sliders,
    title: "Clean mobile-first dashboard",
    body: "Filter by status (Broken Out / Approaching / Breakdown / Consolidating), see a sparkline, volume-vs-20-day-average, and distance to breakout in one glance. Looks great on phone and desktop.",
  },
  {
    icon: InfinityIcon,
    title: "Fully customisable brand",
    body: "Set your own brand name, tagline, disclaimer and accent colour from Settings. Every email, header and footer updates instantly to match.",
  },
];

const HOW_IT_WORKS = [
  { step: "1", title: "Add tickers", body: "Search any stock symbol and add it to your watchlist in one tap." },
  { step: "2", title: "Auto levels", body: "The app computes breakout, support and three sell targets from the last 20 and 50 trading days." },
  { step: "3", title: "Monitor", body: "A clean dashboard shows live prices, volume, status badges and a sparkline for every stock." },
  { step: "4", title: "Get alerted", body: "In-app toasts and emails fire the moment levels are hit or significant news breaks." },
  { step: "5", title: "Act with confidence", body: "You decide — the app surfaces the information so you can make an informed call." },
];

export default function About() {
  const [brand, setBrand] = useState({
    name: "LaukStocks",
    tagline: "Stock analyzer helping you make more informed decisions",
    disclaimer: "This is not financial advice, always do your own research before buying any stocks.",
    color: "#6366F1",
  });

  useEffect(() => {
    api.get("/config").then(r => { if (r.data?.brand) setBrand(r.data.brand); }).catch(() => {});
  }, []);

  const accent = brand.color || "#6366F1";

  return (
    <div className="grain min-h-screen relative" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F17]/80 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/about" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center border" style={{ background: `${accent}18`, borderColor: `${accent}55` }}>
              <BrandLogo color={accent} size={26} />
            </div>
            <div>
              <div className="font-head font-extrabold text-base sm:text-lg leading-none">{brand.name}</div>
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-0.5 hidden sm:block">About</div>
            </div>
          </Link>
          <Link to="/">
            <Button className="h-8 text-white hover:opacity-90" style={{ backgroundColor: accent }} data-testid="open-app-btn">
              Open the app <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 relative z-10">
        {/* Hero */}
        <section className="text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center border" style={{ background: `${accent}20`, borderColor: `${accent}55` }}>
              <BrandLogo color={accent} size={56} />
            </div>
          </div>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            {brand.name}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed" data-testid="hero-tagline">
            {brand.tagline}
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <Link to="/">
              <Button size="lg" className="text-white hover:opacity-90 h-11 px-6" style={{ backgroundColor: accent }} data-testid="hero-cta">
                <Zap className="w-4 h-4 mr-2" />
                Launch dashboard
              </Button>
            </Link>
            <a href="#features">
              <Button size="lg" variant="outline" className="h-11 px-6 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-slate-100">
                See features
              </Button>
            </a>
          </div>
          <div className="mt-10 text-[10px] font-mono text-slate-600 uppercase tracking-widest">
            Yahoo Finance · Unlimited · 60s refresh · No account needed
          </div>
        </section>

        {/* Features */}
        <section id="features" className="mt-20 sm:mt-28">
          <div className="text-center max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: accent }}>What you get</div>
            <h2 className="font-head font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Everything you need to spot and trade breakouts</h2>
            <p className="mt-3 text-slate-400 text-sm sm:text-base">Clean, mobile-first, real data, and alerts that respect your inbox.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="rounded-2xl border border-slate-800 bg-[#121824] p-5 hover:border-slate-700 transition-colors"
                data-testid={`feature-${i}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 border" style={{ background: `${accent}18`, borderColor: `${accent}44` }}>
                  <f.icon className="w-5 h-5" style={{ color: accent }} />
                </div>
                <div className="font-head font-bold text-lg leading-tight">{f.title}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mt-20 sm:mt-28">
          <div className="text-center max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: accent }}>How it works</div>
            <h2 className="font-head font-bold text-3xl sm:text-4xl mt-2 tracking-tight">From ticker to signal in minutes</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-10">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="rounded-2xl border border-slate-800 bg-[#121824] p-4" data-testid={`step-${s.step}`}>
                <div className="font-mono font-bold text-2xl mb-1" style={{ color: accent }}>{s.step}</div>
                <div className="font-head font-semibold text-base leading-tight">{s.title}</div>
                <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mt-20 sm:mt-28">
          <div className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: `${accent}55`, background: `${accent}10` }} data-testid="disclaimer-section">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: accent }}>Important disclaimer</div>
                <p className="text-slate-200 text-sm sm:text-base leading-relaxed font-mono italic">
                  {brand.disclaimer}
                </p>
                <p className="text-slate-500 text-xs mt-3 leading-relaxed">
                  {brand.name} is a technical-analysis tool only. We surface price levels, news headlines and earnings dates — we do not provide buy/sell recommendations.
                  Trading stocks involves substantial risk, including the loss of principal. Past performance is not indicative of future results.
                  You are solely responsible for your trading decisions. Always do your own research, verify data independently, and consult a licensed financial advisor before making investment decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-20 sm:mt-24 text-center">
          <h2 className="font-head font-bold text-2xl sm:text-3xl tracking-tight">Ready to track breakouts the smart way?</h2>
          <Link to="/">
            <Button size="lg" className="mt-5 text-white hover:opacity-90 h-11 px-8" style={{ backgroundColor: accent }} data-testid="final-cta">
              Launch {brand.name} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </section>

        <footer className="mt-16 sm:mt-24 pt-6 border-t border-slate-900 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <BrandLogo color={accent} size={20} />
            <span className="font-head font-bold text-sm">{brand.name}</span>
          </div>
          <div className="font-mono text-[10px] text-slate-600 uppercase tracking-widest">© {new Date().getFullYear()} {brand.name} · All rights reserved</div>
        </footer>
      </main>
    </div>
  );
}
